import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { AuthAPI } from '../api/auth';

export interface User {
  id: string;
  name: string;
  email: string;
  iconUrl?: string;
}

export interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

interface AuthContextType extends AuthState {
  login: () => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    error: null,
  });

  const login = async () => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true, error: null }));
      
      // Use AuthAPI for login (will redirect to Auth0)
      const response = await AuthAPI.login();
      
      if (response.success) {
        // The login will redirect to Auth0, so we don't need to handle the response here
        // The actual authentication will be handled by the callback
      } else {
        throw new Error(response.error || 'Login failed');
      }
    } catch (error) {
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Login failed',
      }));
    }
  };

  const logout = () => {
    // Use AuthAPI for logout (will redirect to Auth0 logout)
    AuthAPI.logout();
    
    setAuthState({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
    });
  };

  const refreshUser = async () => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true }));
      
      const response = await AuthAPI.getCurrentUser();
      console.log('👤 User data response:', response);
      
      if (response.success && response.data) {
        console.log('✅ Setting authenticated user:', response.data);
        setAuthState({
          user: response.data,
          isAuthenticated: true,
          isLoading: false,
          error: null,
        });
      } else {
        throw new Error(response.error || 'Failed to refresh user');
      }
    } catch (error) {
      console.log('❌ Failed to get user data:', error);
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to refresh user',
      }));
    }
  };

  useEffect(() => {
    // Check for existing session on mount
    const checkAuthStatus = async () => {
      console.log('🔍 Checking authentication status...');
      
      try {
        const isAuthenticated = await AuthAPI.checkAuthStatus();
        console.log('🔐 Authentication status:', isAuthenticated);
        
        if (isAuthenticated) {
          console.log('✅ User is authenticated, getting user data...');
          await refreshUser();
        } else {
          console.log('❌ User is not authenticated');
          setAuthState(prev => ({ ...prev, isLoading: false }));
        }
      } catch (error) {
        console.error('🚨 Auth check failed:', error);
        setAuthState(prev => ({ ...prev, isLoading: false }));
      }
    };

    checkAuthStatus();
  }, []);

  const value: AuthContextType = {
    ...authState,
    login,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
